import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';
import FilterTabs from '../../components/FilterTabs';
import BookingCard from '../../components/BookingCard';
import BookingCardMobile from '../../components/BookingCardMobile';
import BookingDialog from '../../components/BookingDialog';
import AppointmentActionDialog from '../../components/AppointmentActionDialog';
import SessionDocumentsDialog from '../../components/SessionDocumentsDialog';
import useIsMobile from '../../components/shared/useIsMobile';
import {
  getMemberAppointments,
  cancelAppointment,
  clearCancelError,
} from '../../api/Appointments';
import { getCounselorList } from '../../api/CounselorList';

/**
 * MyBookings Component
 */
const MyBookings = () => {
  const dispatch = useDispatch();
  const [collapsed, setCollapsed] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [currentTime, setCurrentTime] = useState(moment());
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedCounselor, setSelectedCounselor] = useState(null);
  const [rebookMode, setRebookMode] = useState({ isRebook: false, existingBooking: null });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, booking: null });
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedAppointmentForDocs, setSelectedAppointmentForDocs] = useState(null);
  const isMobile = useIsMobile(768); // Use 768px breakpoint for tablet/mobile

  // Get appointments from Redux store
  const { appointments, loading, error, cancelLoading, cancelError } = useSelector(
    (state) => state.memberAppointments
  );

  // Get counselor list from Redux store (same as Dashboard)
  const { data: counselorData } = useSelector((state) => state.counselorList);

  // Appointment status constants
  const APPOINTMENT_STATUS = {
    SCHEDULED: "SCHEDULED",
    IN_PROGRESS: "IN_PROGRESS",
    PENDING: "PENDING",
    COMPLETED: "COMPLETED",
    IN_COMPLETE: "IN_COMPLETE",
    EXPIRED: "EXPIRED",
    CANCELED: "CANCELED",
    COUNSELOR_UNAVAILABLE: "COUNSELOR_UNAVAILABLE",
  };

  // Fetch appointments when month changes
  useEffect(() => {
    fetchAppointments();
  }, [currentDate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(moment());
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Fetch appointments for selected month
  const fetchAppointments = useCallback(() => {
    const startDate = moment(currentDate).startOf('month').format('YYYY-MM-DD');
    const endDate = moment(currentDate).endOf('month').format('YYYY-MM-DD');

    dispatch(
      getMemberAppointments({
        startDate,
        endDate,
        isRecentOnly: false,
      })
    );
  }, [dispatch, currentDate]);

  // Helper function to get effective status (use currentStatus for SCHEDULED)
  const getEffectiveStatus = (appointment) => {
    if (appointment.status === APPOINTMENT_STATUS.SCHEDULED && appointment.currentStatus) {
      return appointment.currentStatus;
    }
    return appointment.status;
  };

  // Helper function to check if appointment is missed
  const isMissedAppointment = (appointment) => {
    const effectiveStatus = getEffectiveStatus(appointment);
    return (
      effectiveStatus === 'STUDENT MISSED' ||
      effectiveStatus === 'STUDENT_MISSED' ||
      effectiveStatus === 'COUNSELLOR MISSED' ||
      effectiveStatus === 'COUNSELLOR_MISSED' ||
      effectiveStatus === 'MISSED BY COUNSELLOR' ||
      effectiveStatus === 'MISSED BY BOTH' ||
      effectiveStatus === 'MISSED_BY_BOTH'
    );
  };

  // Helper function to check if appointment is incomplete
  const isIncompleteAppointment = (appointment) => {
    const effectiveStatus = getEffectiveStatus(appointment);
    return (
      appointment.status === APPOINTMENT_STATUS.IN_COMPLETE ||
      effectiveStatus === 'INCOMPLETE' ||
      appointment.status === APPOINTMENT_STATUS.IN_PROGRESS ||
      appointment.status === APPOINTMENT_STATUS.PENDING ||
      appointment.status === APPOINTMENT_STATUS.EXPIRED ||
      effectiveStatus === 'RISK MISSING'
    );
  };

  // Filter and categorize appointments
  const filteredAppointments = useMemo(() => {
    if (!Array.isArray(appointments)) return [];

    const now = currentTime;

    const filtered = appointments.filter((appointment) => {
      // Calculate if appointment is past or future
      const endMoment = moment(
        `${appointment.date} ${appointment.endTime}`,
        'YYYY-MM-DD HH:mm:ss'
      );
      const isPast = endMoment.isBefore(now);
      const isFuture = endMoment.isAfter(now);

      // Apply filter based on active tab
      if (activeFilter === 'All') return true;

      if (activeFilter === 'Upcoming') {
        return (
          [APPOINTMENT_STATUS.SCHEDULED, APPOINTMENT_STATUS.IN_PROGRESS, APPOINTMENT_STATUS.PENDING].includes(
            appointment.status
          ) && isFuture && !isMissedAppointment(appointment)
        );
      }

      if (activeFilter === 'Completed') {
        return appointment.status === APPOINTMENT_STATUS.COMPLETED;
      }

      if (activeFilter === 'Missed') {
        return isMissedAppointment(appointment);
      }

      if (activeFilter === 'Incomplete') {
        return isIncompleteAppointment(appointment);
      }

      if (activeFilter === 'Cancelled') {
        return [APPOINTMENT_STATUS.CANCELED, APPOINTMENT_STATUS.COUNSELOR_UNAVAILABLE].includes(
          appointment.status
        );
      }

      return true;
    });

    // Sort appointments: Upcoming first, then by date/time
    return filtered.sort((a, b) => {
      const aEndMoment = moment(`${a.date} ${a.endTime}`, 'YYYY-MM-DD HH:mm:ss');
      const bEndMoment = moment(`${b.date} ${b.endTime}`, 'YYYY-MM-DD HH:mm:ss');
      const aIsFuture = aEndMoment.isAfter(now);
      const bIsFuture = bEndMoment.isAfter(now);

      const aIsUpcoming = [APPOINTMENT_STATUS.SCHEDULED, APPOINTMENT_STATUS.IN_PROGRESS, APPOINTMENT_STATUS.PENDING].includes(a.status) && aIsFuture;
      const bIsUpcoming = [APPOINTMENT_STATUS.SCHEDULED, APPOINTMENT_STATUS.IN_PROGRESS, APPOINTMENT_STATUS.PENDING].includes(b.status) && bIsFuture;

      // Upcoming appointments first
      if (aIsUpcoming && !bIsUpcoming) return -1;
      if (!aIsUpcoming && bIsUpcoming) return 1;

      // Then sort by date and time (ascending for upcoming, descending for past)
      const aDateTime = moment(`${a.date} ${a.startTime}`, 'YYYY-MM-DD HH:mm:ss');
      const bDateTime = moment(`${b.date} ${b.startTime}`, 'YYYY-MM-DD HH:mm:ss');

      if (aIsUpcoming && bIsUpcoming) {
        // For upcoming: earliest first
        return aDateTime.diff(bDateTime);
      } else {
        // For past/completed: most recent first
        return bDateTime.diff(aDateTime);
      }
    });
  }, [appointments, activeFilter, currentTime]);

  // Calculate counts for filter tabs
  const filterCounts = useMemo(() => {
    if (!Array.isArray(appointments)) {
      return { all: 0, upcoming: 0, completed: 0, missed: 0, incomplete: 0, cancelled: 0 };
    }

    const now = currentTime;
    let upcoming = 0;
    let completed = 0;
    let missed = 0;
    let incomplete = 0;
    let cancelled = 0;

    appointments.forEach((appointment) => {
      const endMoment = moment(
        `${appointment.date} ${appointment.endTime}`,
        'YYYY-MM-DD HH:mm:ss'
      );
      const isFuture = endMoment.isAfter(now);

      // Check for missed appointments first
      if (isMissedAppointment(appointment)) {
        missed++;
      }
      // Then check for incomplete
      else if (isIncompleteAppointment(appointment)) {
        incomplete++;
      }
      // Then check for upcoming
      else if (
        [APPOINTMENT_STATUS.SCHEDULED, APPOINTMENT_STATUS.IN_PROGRESS, APPOINTMENT_STATUS.PENDING].includes(
          appointment.status
        ) &&
        isFuture
      ) {
        upcoming++;
      }
      // Then check for completed
      else if (appointment.status === APPOINTMENT_STATUS.COMPLETED) {
        completed++;
      }
      // Finally check for cancelled
      else if (
        [APPOINTMENT_STATUS.CANCELED, APPOINTMENT_STATUS.COUNSELOR_UNAVAILABLE].includes(
          appointment.status
        )
      ) {
        cancelled++;
      }
    });

    return {
      all: appointments.length,
      upcoming,
      completed,
      missed,
      incomplete,
      cancelled,
    };
  }, [appointments, currentTime]);

  // Handle cancel appointment with confirmation
  const handleCancel = (booking) => {
    setConfirmDialog({
      open: true,
      type: 'cancel',
      booking: booking,
    });
  };

  const confirmCancelAppointment = async () => {
    const booking = confirmDialog.booking;
    setConfirmDialog({ open: false, type: null, booking: null });

    try {
      await dispatch(
        cancelAppointment({ appointmentId: booking.appointmentId })
      ).unwrap();

      // Show success dialog
      setConfirmDialog({
        open: true,
        type: 'cancelSuccess',
        booking: booking,
      });

      // Refresh appointments to show updated status
      fetchAppointments();
    } catch (error) {
      toast.error(error || 'Failed to cancel appointment', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  // Handle reschedule - Opens BookingDialog in reschedule mode to update existing appointment
  const handleReschedule = (booking) => {
    // Map booking data to counselor format for BookingDialog
    const counselorData = {
      counsellorLongId: booking.counsellorId,
      counsellorUserId: booking.counsellorUserId, // Numeric user ID for API
      pcUserId: booking.counsellorUserId || booking.counsellorId,
      id: booking.counsellorUserId || booking.counsellorId,
      firstName: booking.counsellorFirstName,
      lastName: booking.counsellorLastName,
      title: booking.counsellorTitle || 'Dr.',
      imageURL: booking.counsellorImageURL,
      specializations: booking.counsellorSpecialization ? [booking.counsellorSpecialization] : [],
      degree: booking.counsellorDegree ? [booking.counsellorDegree] : [],
      rating: booking.counsellorRating,
      experience: booking.counsellorExperience,
      languages: booking.counsellorLanguages ? booking.counsellorLanguages.split(',').map(l => l.trim()) : [],
      modes: booking.mode ? [booking.mode] : ['VIDEO', 'AUDIO', 'CHAT', 'IN_PERSON'],
    };

    setSelectedCounselor({ counselor: counselorData, existingAppointment: booking, isReschedule: true });
    setBookingDialogOpen(true);
  };

  // Handle booking dialog close
  const handleBookingDialogClose = () => {
    setBookingDialogOpen(false);
    setSelectedCounselor(null);
    // Refresh appointments after reschedule
    fetchAppointments();
  };

  // Handle join now
  const handleJoinNow = (booking) => {
    // Navigate to video/audio/chat based on mode
    if (booking.mode === 'VIDEO') {
      window.open(`/member/video-call/${booking.appointmentId}`, '_blank');
    } else if (booking.mode === 'AUDIO') {
      window.open(`/member/audio-call/${booking.appointmentId}`, '_blank');
    } else if (booking.mode === 'CHAT') {
      window.open(`/member/chat/${booking.appointmentId}`, '_blank');
    }
  };

  // Handle rebook - Opens BookingDialog in booking mode to create new appointment
  const handleRebook = async (booking) => {
    let counselors = [];

    if (counselorData?.status === 'success' && counselorData?.message?.payload?.data) {
      counselors = counselorData.message.payload.data;
    } else {
      // Fetch counselor list
      const result = await dispatch(getCounselorList({ limit: 50, offset: 0 })).unwrap();
      if (result?.status === 'success' && result?.message?.payload?.data) {
        counselors = result.message.payload.data;
      }
    }

    const matchedCounselor = counselors.find(c => c.pcUserId === booking.counsellorId);

    if (matchedCounselor) {
      const counselorData = {
        pcUserId: matchedCounselor.pcUserId || null,
        counsellorLongId: matchedCounselor.counsellorLongId || matchedCounselor.id,
        id: matchedCounselor.id || null,
        imageURL: matchedCounselor.imageUrl || null,
        firstName: matchedCounselor.firstName,
        lastName: matchedCounselor.lastName,
        title: matchedCounselor.title || 'Dr.',
        experience: matchedCounselor.experience || null,
        rating: matchedCounselor.averageRating || null,
        languages: matchedCounselor.languages ? matchedCounselor.languages.split(',').map(l => l.trim()) : [],
        degree: matchedCounselor.degree && Array.isArray(matchedCounselor.degree) && matchedCounselor.degree.length > 0 ? matchedCounselor.degree : null,
        specializations: matchedCounselor.specialization ? [matchedCounselor.specialization] : [],
        modes: matchedCounselor.modes || null,
      };

      console.log('Rebook - Matched counselor from list:', counselorData);

      setSelectedCounselor({ counselor: counselorData, existingAppointment: booking, isReschedule: false });
      setBookingDialogOpen(true);
    } else {
      toast.error('Counselor not found. Please try again.');
    }
  };

  // Handle view documents
  const handleViewDocuments = (booking) => {
    setSelectedAppointmentForDocs(booking);
    setDocumentsDialogOpen(true);
  };

  // Handle clear cancel error
  useEffect(() => {
    if (cancelError) {
      dispatch(clearCancelError());
    }
  }, [cancelError, dispatch]);

  // Month navigation functions
  const getMonthName = () => {
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    const date = new Date(currentDate);
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  const goToPreviousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  // Get dialog content based on type
  const getDialogContent = () => {
    const { type, booking } = confirmDialog;

    if (!booking) return {};

    const practitionerName = `${booking.counsellorTitle || 'Dr.'} ${booking.counsellorFirstName} ${booking.counsellorLastName}`;
    const appointmentDate = moment(booking.date).format('Do MMM YYYY');
    const appointmentTime = `${moment(booking.startTime, 'HH:mm:ss').format('h:mm a')} - ${moment(booking.endTime, 'HH:mm:ss').format('h:mm a')}`;

    switch (type) {
      case 'cancel':
        return {
          type: 'confirm',
          title: 'Cancel Appointment?',
          message: 'Are you sure you want to cancel this appointment?',
          actionText: 'Yes, Cancel',
          onAction: confirmCancelAppointment,
        };
      case 'cancelSuccess':
        return {
          type: 'success',
          title: 'Cancelled!',
          message: 'Your appointment has been successfully cancelled.',
          actionText: 'View Booking',
          onAction: () => setConfirmDialog({ open: false, type: null, booking: null }),
        };
      case 'bookingSuccess':
        return {
          type: 'success',
          title: 'Successful...!',
          message: 'Your appointment has been successfully booked.',
          actionText: 'View Booking',
          onAction: () => {
            setConfirmDialog({ open: false, type: null, booking: null });
          },
        };
      case 'rescheduleSuccess':
        return {
          type: 'success',
          title: 'Rescheduled!',
          message: 'Your appointment has been successfully rescheduled.',
          actionText: 'View Booking',
          onAction: () => setConfirmDialog({ open: false, type: null, booking: null }),
        };
      case 'rebookSuccess':
        return {
          type: 'success',
          title: 'Rebooked!',
          message: 'Your appointment has been successfully rebooked.',
          actionText: 'View Booking',
          onAction: () => setConfirmDialog({ open: false, type: null, booking: null }),
        };
      default:
        return {};
    }
  };

  const dialogContent = getDialogContent();

  return (
    <>
      {/* Appointment Action Dialogs */}
      <AppointmentActionDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: null, booking: null })}
        {...dialogContent}
      />

      {/* Session Documents Dialog */}
      <SessionDocumentsDialog
        open={documentsDialogOpen}
        onClose={() => {
          setDocumentsDialogOpen(false);
          setSelectedAppointmentForDocs(null);
        }}
        appointment={selectedAppointmentForDocs}
      />

      {/* Booking Dialog - For booking and rescheduling appointments */}
      <BookingDialog
        open={bookingDialogOpen}
        onClose={handleBookingDialogClose}
        counselor={selectedCounselor?.counselor}
        onBookingSuccess={(booking, actionType) => {
          handleBookingDialogClose();
          // Show success dialog based on action type
          setConfirmDialog({
            open: true,
            type: actionType === 'reschedule' ? 'rescheduleSuccess' : actionType === 'rebook' ? 'rebookSuccess' : 'bookingSuccess',
            booking: booking || {},
          });
        }}
        isReschedule={selectedCounselor?.isReschedule || false}
        existingAppointment={selectedCounselor?.existingAppointment || null}
      />

      <div className="relative flex min-h-screen bg-[#F8FAFC]">
        <TopBar />

      {/* Sidebar - Only visible on desktop */}
      <div className={`hidden lg:flex ${collapsed ? "w-[64px]" : "w-[272px]"} fixed top-0 left-0 h-screen z-20`}>
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 min-w-0 bg-[#F8FAFC] pt-16 px-2 pb-8 transition-all duration-300 ml-0 ${
          collapsed ? "lg:ml-[64px]" : "lg:ml-[272px]"
        }`}
      >
        <div className="w-full h-full pt-2">
          <div className="w-full h-full p-4 md:p-6 bg-[#FFFFFF] min-h-screen rounded-2xl">
          <div className="w-full max-w-[1100px] mx-auto flex flex-col gap-4 md:gap-5">
          {/* Page Header and Filter Tabs */}
          <div className="flex flex-col gap-4">
            {/* Page Header */}
            <h1 className="text-xl font-bold text-[#171717] font-poppins mb-0">My Bookings</h1>

            {/* Month Selector */}
            <div className="flex items-center justify-between bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl shadow-sm p-2 md:p-3">
              <button
                onClick={goToPreviousMonth}
                className="flex items-center justify-center w-[32px] h-[32px] p-0 bg-[#F1F5F9] border-0 hover:bg-[#E2E8F0] rounded-full transition-colors"
              >
                <ChevronLeft size={18} className="text-[#64748B]" />
              </button>
              <span className="text-sm md:text-base font-semibold text-[#171717] font-poppins min-w-[100px] md:min-w-[120px] text-center">
                {getMonthName()}
              </span>
              <button
                onClick={goToNextMonth}
                className="flex items-center justify-center w-[32px] h-[32px] p-0 bg-[#F1F5F9] border-0 hover:bg-[#E2E8F0] rounded-full transition-colors"
              >
                <ChevronRight size={18} className="text-[#64748B]" />
              </button>
            </div>

            {/* Filter Tabs */}
            <div className="overflow-x-auto scrollbar-hide -mx-3 md:mx-0 px-3 md:px-0">
              <FilterTabs
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
                counts={filterCounts}
              />
            </div>
          </div>

            {/* Appointments List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2563EB] mx-auto"></div>
                    <p className="mt-4 text-sm text-[#334155] font-poppins">Loading bookings...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <p className="text-base text-[#DC2626] font-poppins">Failed to load bookings</p>
                    <p className="text-sm text-[#64748B] font-poppins mt-2">{error}</p>
                    <button
                      onClick={fetchAppointments}
                      className="mt-4 btn-gradient rounded-xl h-9 px-[14px] text-[13px] font-semibold border-0 font-poppins"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              ) : filteredAppointments.length > 0 ? (
                <div className="flex flex-col gap-3 md:gap-4">
                  {filteredAppointments.map((booking) => (
                    isMobile ? (
                      <BookingCardMobile
                        key={booking.appointmentId}
                        booking={booking}
                        onCancel={handleCancel}
                        onReschedule={handleReschedule}
                        onJoinNow={handleJoinNow}
                        onRebook={handleRebook}
                        onViewDocuments={handleViewDocuments}
                        currentTime={currentTime}
                      />
                    ) : (
                      <BookingCard
                        key={booking.appointmentId}
                        booking={booking}
                        onCancel={handleCancel}
                        onReschedule={handleReschedule}
                        onJoinNow={handleJoinNow}
                        onRebook={handleRebook}
                        onViewDocuments={handleViewDocuments}
                        currentTime={currentTime}
                      />
                    )
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <p className="text-base text-[#334155] font-poppins">
                      No bookings found for {activeFilter.toLowerCase()} appointments
                    </p>
                    <p className="text-sm text-[#64748B] font-poppins mt-2">
                      {activeFilter === 'All' && 'Book your first appointment with a practitioner'}
                      {activeFilter === 'Upcoming' && 'You have no upcoming appointments'}
                      {activeFilter === 'Completed' && 'You have no completed appointments'}
                      {activeFilter === 'Missed' && 'You have no missed appointments'}
                      {activeFilter === 'Incomplete' && 'You have no incomplete appointments'}
                      {activeFilter === 'Cancelled' && 'You have no cancelled appointments'}
                    </p>
                    {activeFilter === 'All' && (
                      <button
                        onClick={() => (window.location.href = '/member/dashboard')}
                        className="mt-4 btn-gradient rounded-xl h-9 px-[14px] text-[13px] font-semibold border-0 font-poppins"
                      >
                        Find a Practitioner
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default MyBookings;
